/**
 * Pilot requests (F4).
 *
 * The child collections have their own calls rather than riding on `updatePilot`,
 * mirroring the API: a KPI table save is one intent, and folding it into the
 * pilot's own PATCH would make a rename and a status change indistinguishable in
 * the audit trail.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.2, PRD §5.4
 */
import type {
  CreateBlockerRequest,
  CreatePilotRequest,
  ListPilotsQuery,
  PilotDetail,
  PilotSummary,
  PilotTimeline,
  ReplaceKpisRequest,
  ReplaceMilestonesRequest,
  ResolveBlockerRequest,
  UpdatePilotRequest,
} from '@nexus/contracts';

import { api } from '../../../lib/api-client';
import type { PageMeta } from '../../../lib/pagination';

/** A page of pilots. */
export interface PilotPage {
  data: PilotSummary[];
  meta: PageMeta;
}

export async function listPilots(query: ListPilotsQuery): Promise<PilotPage> {
  return api.get<PilotPage>('/pilots', { query });
}

/** The portfolio timeline / Gantt (F16). */
export async function getPilotTimeline(): Promise<PilotTimeline> {
  return api.get<PilotTimeline>('/pilots/timeline');
}

export async function getPilot(id: string): Promise<PilotDetail> {
  return api.get<PilotDetail>(`/pilots/${id}`);
}

export async function createPilot(body: CreatePilotRequest): Promise<PilotDetail> {
  return api.post<PilotDetail>('/pilots', body);
}

export async function updatePilot(
  id: string,
  body: UpdatePilotRequest,
): Promise<PilotDetail> {
  return api.patch<PilotDetail>(`/pilots/${id}`, body);
}

/** Replaces the whole KPI table. The array's order is the display order. */
export async function replaceKpis(
  id: string,
  body: ReplaceKpisRequest,
): Promise<PilotDetail> {
  return api.put<PilotDetail>(`/pilots/${id}/kpis`, body);
}

/** Replaces the whole milestone list. */
export async function replaceMilestones(
  id: string,
  body: ReplaceMilestonesRequest,
): Promise<PilotDetail> {
  return api.put<PilotDetail>(`/pilots/${id}/milestones`, body);
}

/** Appends one blocker. */
export async function addBlocker(
  id: string,
  body: CreateBlockerRequest,
): Promise<PilotDetail> {
  return api.post<PilotDetail>(`/pilots/${id}/blockers`, body);
}

/** Resolves or reopens one blocker. */
export async function resolveBlocker(
  id: string,
  blockerId: string,
  body: ResolveBlockerRequest,
): Promise<PilotDetail> {
  return api.patch<PilotDetail>(`/pilots/${id}/blockers/${blockerId}`, body);
}
